import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Paper,
  Grid,
  Typography,
  CircularProgress,
  Modal,
  IconButton,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { HttpService } from "../../service/HttpService";
import { useAuth } from "../../context/AuthProvider";
import "./COAReportArchive.scss";
import { RxCross1 } from "react-icons/rx";
import { GoTrash } from "react-icons/go";
import DeleteModal from "../../components/Model/DeleteModal";
import { toast } from "react-toastify";

const COAReportArchive = ({ roomId, harvestedDate }) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  const [allData, setAllData] = useState([]);
  const [selectedBatchTab, setSelectedBatchTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalRows, setModalRows] = useState([]);
  const [modalColumns, setModalColumns] = useState([]);

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Use single API call as requested: coa/grouped?roomId=X&harvestedDate=Y&archived=true
      // const res = await httpService.get(`coa/grouped?roomId=${roomId}&harvestedDate=${harvestedDate}&archived=true`);
      const res = await httpService.get(
        `coa/grouped?room=${roomId}&harvestDate=${harvestedDate}&archived=true`,
      );
      if (res?.success && Array.isArray(res.data)) {
        setAllData(res.data);
      } else if (res?.success && res.data) {
        // Fallback if it returns a single object instead of array
        setAllData([res.data]);
      }
    } catch (error) {
      console.error("Error fetching grouped COA data:", error);
      setAllData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (roomId && harvestedDate) {
      fetchReportData();
    }
  }, [roomId, harvestedDate]);

  const handleBatchTabChange = (event, newValue) => {
    setSelectedBatchTab(newValue);
  };

  const handleDelete = async () => {
    if (!selectedBatchId) return;
    setLoading(true);
    try {
      const res = await httpService.delete(`coa/delete/${selectedBatchId}`);
      if (res?.success) {
        toast.success("Record deleted successfully");
        fetchReportData();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Error deleting COA record:", error);
      toast.error("Delete failed");
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setSelectedBatchId(null);
    }
  };

  if (loading && allData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allData.length) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No COA Data Found
        </Typography>
      </Box>
    );
  }

  const currentReport = allData[selectedBatchTab] || {};
  const batch = currentReport.batch || {};
  const analytes = currentReport.analytes || [];
  // const summary = analytes.summary || {};
  const microbials = analytes.microbials || [];
  const waterActivity = analytes.water_activity || [];
  const potency = analytes.potency || [];
  const terpenesSummary = analytes.terpenes_summary || [];
  const yeastMold = analytes.yeast_mold || [];
  const heavyMetals = analytes.heavy_metals || [];
  const filthForeignMaterial = analytes.filth_foreign || [];
  const totalColiform = analytes.total_coliform || [];
  const pesticides = analytes.pesticides || [];

  const commonGridSx = {
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      whiteSpace: "normal",
      lineHeight: "1.2",
    },
    "& .MuiDataGrid-cell": {
      border: "1px solid #e0e0e0",
      whiteSpace: "normal",
      lineHeight: "1.2",
      display: "flex",
      alignItems: "center",
      py: 1,
    },
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
    "& .status-pass": {
      color: "#2e7d32",
      fontWeight: 600,
    },
    "& .status-fail": {
      color: "#d32f2f",
      fontWeight: 600,
    },
    "& .status-warning": {
      color: "#eea70fff",
      fontWeight: 600,
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "#f5f5f5",
    },
  };

  const getCommonValue = (params, activeField) => {
    const row = params.row;
    const actionLevelVal = row.action_level;
    const resultVal = row.result;
    const loqVal = row.loq;

    if (activeField === "action_level") {
      return (
        actionLevelVal?.aw ||
        actionLevelVal?.ppm ||
        actionLevelVal?.percent ||
        actionLevelVal?.cfu_g ||
        actionLevelVal?.aw_text ||
        actionLevelVal?.ppm_text ||
        actionLevelVal?.percent_text ||
        actionLevelVal?.cfu_g_text ||
        actionLevelVal?.text ||
        actionLevelVal?.action_level ||
        actionLevelVal?.action_level_text ||
        "0"
      );
    }
    if (activeField === "result") {
      return (
        resultVal?.aw ||
        resultVal?.ppm ||
        resultVal?.percent ||
        resultVal?.cfu_g ||
        resultVal?.aw_text ||
        resultVal?.ppm_text ||
        resultVal?.percent_text ||
        resultVal?.cfu_g_text ||
        resultVal?.text ||
        resultVal?.result_text ||
        resultVal?.result ||
        "0"
      );
    }
    if (activeField === "loq") {
      return (
        loqVal?.aw ||
        loqVal?.ppm ||
        loqVal?.percent ||
        loqVal?.cfu_g ||
        loqVal?.aw_text ||
        loqVal?.ppm_text ||
        loqVal?.percent_text ||
        loqVal?.cfu_g_text ||
        loqVal?.text ||
        loqVal?.loq_text ||
        loqVal?.loq ||
        "0"
      );
    }
    return "--";
  };

  const hasPositiveResult = (analytesArray) => {
    if (!analytesArray || !Array.isArray(analytesArray)) return false;
    return analytesArray.some((item) => {
      const val = getCommonValue({ row: item }, "result");
      if (typeof val === "number") return val > 0;
      if (typeof val === "string") {
        const lowerVal = val.trim().toLowerCase();
        if (
          lowerVal === "nd" ||
          lowerVal === "0" ||
          lowerVal === "<loq" ||
          lowerVal === "-"
        ) {
          return false;
        }
        const parsed = parseFloat(val);
        return !isNaN(parsed) && parsed > 0;
      }
      return false;
    });
  };

  const getStatusClass = (status, analytesArray, category = "") => {
    const s = (status || "").toLowerCase();
    const isYeastMold = category === "yeast_mold";

    if (s === "passed" || s === "pass") {
      if (isYeastMold) {
        const hasHighResult = analytesArray.some((item) => {
          const val = getCommonValue({ row: item }, "result");
          const num = parseFloat(val);
          return !isNaN(num) && num >= 99900 && num < 100000;
        });
        return hasHighResult ? "status-warning" : "status-pass";
      }
      return hasPositiveResult(analytesArray)
        ? "status-warning"
        : "status-pass";
    }
    return "status-fail";
  };

  const waterActivityColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "action_level",
      headerName: "Action Level (aw)",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.action_level?.aw ||
        params?.row?.action_level?.aw_text ||
        "--",
    },
    {
      field: "result",
      headerName: "Result (aw)",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.result?.aw || params?.row?.result?.aw_text || "--",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params?.value}
        </span>
      ),
    },
  ];

  const potencyColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "loq",
      headerName: "LOQ %",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.loq?.percent || params?.row?.loq?.percent_text || "--",
    },
    {
      field: "result_mg_g",
      headerName: "Result mg/g",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.result?.mg_g || params?.row?.result?.mg_g_text || "--",
    },
    {
      field: "result_percent",
      headerName: "Result %",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.result?.percent ||
        params?.row?.result?.percent_text ||
        "--",
    },
  ];

  const terpenesSummaryColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "result_percent",
      headerName: "Result %",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.result?.percent ||
        params?.row?.result?.percent_text ||
        "--",
    },
    {
      field: "result_mg_g",
      headerName: "Result mg/g",
      flex: 1,
      valueGetter: (params) =>
        params?.row?.result?.mg_g || params?.row?.result?.mg_g_text || "--",
    },
  ];

  const microbialsColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "action_level",
      headerName: "Action Level",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "action_level"),
    },
    {
      field: "result",
      headerName: "Result",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "result"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "passed" ||
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params?.value}
        </span>
      ),
    },
  ];

  const yeastMoldColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "action_level",
      headerName: "Action Level",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "action_level"),
    },
    {
      field: "result",
      headerName: "Result",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "result"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "passed" ||
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params?.value}
        </span>
      ),
    },
  ];

  const heavyMetalsColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "loq",
      headerName: "LOQ (ppm)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "loq"),
    },
    {
      field: "action_level",
      headerName: "Action Level (ppm)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "action_level"),
    },
    {
      field: "result",
      headerName: "Result (ppm)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "result"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "passed" ||
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params?.value}
        </span>
      ),
    },
  ];

  const filthForeignColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "action_level",
      headerName: "Action Level (%)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "action_level"),
    },
    {
      field: "result",
      headerName: "Result (%)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "result"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "passed" ||
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params?.value}
        </span>
      ),
    },
  ];

  const totalColiformColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "action_level",
      headerName: "Action Level (cfu/g)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "action_level"),
    },
    {
      field: "result",
      headerName: "Result (cfu/g)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "result"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "passed" ||
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params.value}
        </span>
      ),
    },
  ];

  const pesticidesColumns = [
    { field: "analyte_name", headerName: "Analyte", minWidth: 200, flex: 1 },
    {
      field: "loq",
      headerName: "LOQ (ppm)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "loq"),
    },
    {
      field: "action_level",
      headerName: "Action Level (ppm)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "action_level"),
    },
    {
      field: "result",
      headerName: "Result (ppm)",
      flex: 1,
      valueGetter: (params) => getCommonValue(params, "result"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          className={
            params?.value?.toLowerCase() === "passed" ||
            params?.value?.toLowerCase() === "pass"
              ? "status-pass"
              : "status-fail"
          }
        >
          {params?.value}
        </span>
      ),
    },
  ];

  const handleOpenModal = (title, rows, columns) => {
    setModalTitle(title);
    setModalRows(rows);
    setModalColumns(columns);
    setOpenModal(true);
  };

  return (
    <div className="coa-report-archive">
      <Tabs
        value={selectedBatchTab}
        onChange={handleBatchTabChange}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          backgroundColor: "#fffffc",
          "& .MuiTabs-indicator": {
            backgroundColor: "#00A272",
            height: "3px",
          },
          "& .MuiTab-root": {
            position: "relative",
            color: "#A1A2A1",
            fontWeight: 400,
            fontSize: "16px",
            textTransform: "none",
            padding: "12px 20px",
            marginRight: "8px",
            "&:not(.Mui-selected)::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "3px",
              backgroundColor: "#C4C4C4",
            },
            "&.Mui-selected": {
              color: "#00A272 !important",
              fontWeight: 600,
              backgroundColor: "#E6F6F1",
              borderRadius: "4px 4px 0 0",
            },
          },
          // ".MuiTabs-flexContainer": {
          //   display: "flex",
          //   justifyContent: "flex-start",
          // },
          mb: 3,
        }}
      >
        {allData.map((b, index) => (
          <Tab key={index} label={b.batch_number} />
        ))}
      </Tabs>

      <Paper className="report-paper" elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            className="report-title pb-0 mb-0"
            sx={{ mb: 0 }}
          >
            {batch.strain}
          </Typography>
          <Button
            className="btn btn-delete"
            onClick={() => {
              setSelectedBatchId(batch.id);
              setDeleteModalOpen(true);
            }}
            sx={{ textTransform: "capitalize" }}
          >
            Delete
          </Button>
        </Box>

        {/* Summary Row */}
        <Grid container spacing={0} className="summary-grid">
          <Grid item xs={3} className="summary-col">
            <Typography variant="caption">Total Cannabinoids</Typography>
            <Typography variant="h6">
              {batch.total_cannabinoids_percent || "0.0"}%
            </Typography>
          </Grid>
          <Grid item xs={3} className="summary-col">
            <Typography variant="caption">Total THC</Typography>
            <Typography variant="h6">
              {batch.total_thc_percent || "0.0"}%
            </Typography>
          </Grid>
          <Grid item xs={3} className="summary-col">
            <Typography variant="caption">Total CBD</Typography>
            <Typography variant="h6">
              {batch.total_cbd_percent || "0.0"}%
            </Typography>
          </Grid>
          <Grid item xs={3} className="summary-col">
            <Typography variant="caption">Total Terpenes</Typography>
            <Typography variant="h6">
              {batch.total_terpenes || "0.0"}%
            </Typography>
          </Grid>
        </Grid>

        {/* Status Summary */}
        <Grid container spacing={0} className="microbials-grid">
          <Grid
            item
            xs={2}
            className="micro-col"
            style={{
              cursor:
                batch?.microbials_status?.toLowerCase() !== "passed" ||
                hasPositiveResult(microbials)
                  ? "pointer"
                  : "default",
            }}
            onClick={() =>
              batch?.microbials_status?.toLowerCase() !== "passed" ||
              hasPositiveResult(microbials)
                ? handleOpenModal("Microbials", microbials, microbialsColumns)
                : null
            }
          >
            <Typography variant="caption">Microbials</Typography>
            <Typography
              variant="body1"
              className={getStatusClass(
                batch?.microbials_status,
                microbials,
                "microbials",
              )}
            >
              {batch?.microbials_status || "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            className="micro-col"
            style={{
              cursor:
                getStatusClass(
                  batch?.total_yeast_and_mold_status,
                  yeastMold,
                  "yeast_mold",
                ) !== "status-pass"
                  ? "pointer"
                  : "default",
            }}
            onClick={() =>
              getStatusClass(
                batch?.total_yeast_and_mold_status,
                yeastMold,
                "yeast_mold",
              ) !== "status-pass"
                ? handleOpenModal(
                    "Total Yeast And Mold",
                    yeastMold,
                    yeastMoldColumns,
                  )
                : null
            }
          >
            <Typography variant="caption">Total Yeast And Mold</Typography>
            <Typography
              variant="body1"
              className={getStatusClass(
                batch?.total_yeast_and_mold_status,
                yeastMold,
                "yeast_mold",
              )}
            >
              {batch?.total_yeast_and_mold_status || "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            className="micro-col"
            style={{
              cursor:
                batch?.heavy_metals_status?.toLowerCase() !== "passed" ||
                hasPositiveResult(heavyMetals)
                  ? "pointer"
                  : "default",
            }}
            onClick={() =>
              batch?.heavy_metals_status?.toLowerCase() !== "passed" ||
              hasPositiveResult(heavyMetals)
                ? handleOpenModal(
                    "Heavy Metals",
                    heavyMetals,
                    heavyMetalsColumns,
                  )
                : null
            }
          >
            <Typography variant="caption">Heavy Metals</Typography>
            <Typography
              variant="body1"
              className={getStatusClass(
                batch?.heavy_metals_status,
                heavyMetals,
                "heavy_metals",
              )}
            >
              {batch?.heavy_metals_status || "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            className="micro-col"
            style={{
              cursor:
                batch?.filth_and_foreign_material_status?.toLowerCase() !==
                  "passed" || hasPositiveResult(filthForeignMaterial)
                  ? "pointer"
                  : "default",
            }}
            onClick={() =>
              batch?.filth_and_foreign_material_status?.toLowerCase() !==
                "passed" || hasPositiveResult(filthForeignMaterial)
                ? handleOpenModal(
                    "Filth And Foreign Material",
                    filthForeignMaterial,
                    filthForeignColumns,
                  )
                : null
            }
          >
            <Typography variant="caption">
              Filth And Foreign Material
            </Typography>
            <Typography
              variant="body1"
              className={getStatusClass(
                batch?.filth_and_foreign_material_status,
                filthForeignMaterial,
                "filth_foreign",
              )}
            >
              {batch?.filth_and_foreign_material_status || "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            className="micro-col"
            style={{
              cursor:
                batch?.total_coliform_status?.toLowerCase() !== "passed" ||
                hasPositiveResult(totalColiform)
                  ? "pointer"
                  : "default",
            }}
            onClick={() =>
              batch?.total_coliform_status?.toLowerCase() !== "passed" ||
              hasPositiveResult(totalColiform)
                ? handleOpenModal(
                    "Total Coliform",
                    totalColiform,
                    totalColiformColumns,
                  )
                : null
            }
          >
            <Typography variant="caption">Total Coliform</Typography>
            <Typography
              variant="body1"
              className={getStatusClass(
                batch?.total_coliform_status,
                totalColiform,
                "total_coliform",
              )}
            >
              {batch?.total_coliform_status || "-"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={2}
            className="micro-col"
            style={{
              cursor:
                batch?.pesticides_status?.toLowerCase() !== "passed" ||
                hasPositiveResult(pesticides)
                  ? "pointer"
                  : "default",
            }}
            onClick={() =>
              batch?.pesticides_status?.toLowerCase() !== "passed" ||
              hasPositiveResult(pesticides)
                ? handleOpenModal("Pesticides", pesticides, pesticidesColumns)
                : null
            }
          >
            <Typography variant="caption">Pesticides</Typography>
            <Typography
              variant="body1"
              className={getStatusClass(
                batch?.pesticides_status,
                pesticides,
                "pesticides",
              )}
            >
              {batch?.pesticides_status || "-"}
            </Typography>
          </Grid>
        </Grid>

        {/* Water Activity Table */}
        <div className="table-section">
          <Typography variant="subtitle1" className="section-title">
            Water Activity
          </Typography>
          <Box
            sx={{
              height: waterActivity?.length > 0 ? "auto" : 200,
              width: "100%",
            }}
          >
            <DataGrid
              rows={waterActivity}
              columns={waterActivityColumns}
              getRowId={(row) => row.id}
              autoHeight
              getRowHeight={() => "auto"}
              hideFooter
              sx={commonGridSx}
              showCellVerticalBorder
              showColumnVerticalBorder
            />
          </Box>
        </div>

        {/* Potency Table */}
        <div className="table-section">
          <Typography variant="subtitle1" className="section-title">
            Potency
          </Typography>
          <Box
            sx={{
              height: potency?.length > 0 ? "auto" : 200,
              width: "100%",
            }}
          >
            <DataGrid
              rows={potency}
              columns={potencyColumns}
              getRowId={(row) => row.id}
              autoHeight
              getRowHeight={() => "auto"}
              hideFooter
              sx={commonGridSx}
              showCellVerticalBorder
              showColumnVerticalBorder
            />
          </Box>
        </div>

        {/* Terpenes Summary Table */}
        <div className="table-section">
          <Typography variant="subtitle1" className="section-title">
            Terpenes Summary
          </Typography>
          <Box
            sx={{
              height: terpenesSummary?.length > 0 ? "auto" : 200,
              width: "100%",
            }}
          >
            <DataGrid
              rows={terpenesSummary}
              columns={terpenesSummaryColumns}
              getRowId={(row) => row.id}
              autoHeight
              getRowHeight={() => "auto"}
              hideFooter
              sx={commonGridSx}
              showCellVerticalBorder
              showColumnVerticalBorder
            />
          </Box>
        </div>
      </Paper>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="detail-modal-title"
      >
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">{modalTitle}</h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1 onClick={() => setOpenModal(false)} />
            </div>
          </div>

          <div className="modal_content">
            <div className="modal_content_inner">
              <div className="modal-content mb-4">
                <Box sx={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={modalRows}
                    columns={modalColumns}
                    getRowId={(row) => row.id || row.analyte_name}
                    autoHeight={modalRows.length < 10}
                    getRowHeight={() => "auto"}
                    hideFooter
                    sx={commonGridSx}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <DeleteModal
        open={deleteModalOpen}
        HandleClose={() => {
          setDeleteModalOpen(false);
          setSelectedBatchId(null);
        }}
        HandleDelete={handleDelete}
      />
    </div>
  );
};

export default COAReportArchive;
